import { isEmptyString } from '@/utils/string';
import type { Customer } from './entity';

/**
 * Customer display derivations — how a profile is labelled in the UI (name → identifier fallback).
 * Kept in the `customer` entity so any feature showing a profile derives the label the same way.
 */

// The same keys as the embedded app's customer model, matched case-insensitively as it does.
const FIRST_NAME_KEYS = ['first_name', 'firstname', 'first name'];
const LAST_NAME_KEYS = ['last_name', 'lastname', 'last name', 'surname'];
const FULL_NAME_KEYS = ['full_name', 'fullname', 'full name', 'name'];

function firstAttr(attrs: Record<string, string> | undefined, keys: string[]): string | null {
  if (attrs == null) return null;
  const attributeKeys = Object.keys(attrs);
  for (const key of keys) {
    const attributeKey = attributeKeys.find((candidate) => candidate.toLowerCase() === key);
    const value = attributeKey == null ? undefined : attrs[attributeKey];
    if (!isEmptyString(value ?? '')) return value ?? null;
  }
  return null;
}

/**
 * A customer's name from its attributes: "first last" → a full-name attribute → null.
 * @public
 */
export function customerName(attributes: Record<string, string>): string | null {
  const first = firstAttr(attributes, FIRST_NAME_KEYS);
  const last = firstAttr(attributes, LAST_NAME_KEYS);
  if (first != null && last != null) return `${first} ${last}`;
  return firstAttr(attributes, FULL_NAME_KEYS);
}

/**
 * Fallback identifier label when there's no name: email → phone → "{term} with id {id}".
 * Ported from the Ember `displayIdentifier` getter; the person-detail header uses
 * this directly for the contact line shown beneath the display name.
 * @public
 */
export function customerIdentifierDisplay(customer: Customer, personSingular: string): string {
  const { email: identifierEmail, phone, id } = customer.identifiers ?? {};
  // Ember reads email from `attrs.email` (the customer attributes); fall back to the identifiers
  // email so an address that lives on either source still shows.
  const email = customer.attributes?.email ?? identifierEmail ?? '';
  if (!isEmptyString(email)) return email;
  if (!isEmptyString(phone ?? '')) return phone;
  return `${personSingular} with id ${id ?? customer.id}`;
}

/**
 * Display label for a customer: first/last name → a full-name attribute → email → phone →
 * "{term} with id {id}". `personSingular` is the workspace's people term (e.g. "Person").
 * @public
 */
export function customerDisplay(customer: Customer, personSingular: string): string {
  return customerName(customer.attributes ?? {}) ?? customerIdentifierDisplay(customer, personSingular);
}

/**
 * The secondary contact line shown *beneath a person's full name* in the profile
 * header: email, then phone — but phone only when the workspace promotes phone to
 * an identifier (`canIdentifyByPhone`) — else `null`. Ported from the Ember
 * `profile-name` component, which renders a secondary contact only under a full
 * name and gates the phone branch on the workspace capability. Returns `null`
 * (not a fallback label) so the header can omit the line entirely.
 * @public
 */
export function customerSecondaryContact(
  customer: Pick<Customer, 'attributes'>,
  { canIdentifyByPhone }: { canIdentifyByPhone: boolean },
): string | null {
  const email = customer.attributes?.email ?? '';
  if (!isEmptyString(email)) return email;
  const phone = customer.attributes?.phone ?? '';
  if (canIdentifyByPhone && !isEmptyString(phone)) return phone;
  return null;
}
